import React from 'react';

import {
  Box,
  Stack,
  Typography,
  InputLabel as BaseInputLabel,
  TextField as BaseTextField
} from '@mui/material';
import { styled } from '@mui/material/styles';
import _ from 'lodash';
import {
  Control,
  Controller,
  FieldPath,
  FieldValues,
  UseControllerProps
} from 'react-hook-form';



const InputLabel = styled(BaseInputLabel, {
  shouldForwardProp: prop => prop !== 'isDisabled'
})<{isDisabled?: boolean}>(({ isDisabled }) => ({
  color: '#486C82',
  opacity: isDisabled ? 0.38 : 1,
  fontSize: 12
}));

const CharacterCountTypography = styled(Typography, {
  shouldForwardProp: prop => prop !== 'hasReachedCharacterLimit'
})<{hasReachedCharacterLimit: boolean}>(({ hasReachedCharacterLimit, theme }) => ({
  color: hasReachedCharacterLimit ? '#EB5757' : '#87ACB9',
  fontSize: 12
}));

const CharacterCountBox = styled(Box)({
  '&.MuiBox-root': {
    marginTop: 3
  }
});



export interface ITextFieldProps {
  type?: 'text' | 'email' | 'password' | 'tel' | 'number' | 'date';
  value: string;
  defaultValue?: string;
  onChange: (value: string) => void;
  errorMessage?: string;
  isDisabled?: boolean;
  numLines?: number;
  placeholder?: string;
  startAdornment?: React.ReactNode;
  label?: React.ReactNode;
}

export const TextField: React.FC<ITextFieldProps> = props => {
  const field = (
    <BaseTextField
      type={props.type}
      value={props.value ?? ''}
      defaultValue={props.defaultValue}
      onChange={event => props.onChange(event.target.value)}
      error={!!props.errorMessage}
      helperText={props.errorMessage}
      placeholder={props.placeholder}
      variant="outlined"
      disabled={props.isDisabled}
      multiline={!_.isUndefined(props.numLines) && props.numLines > 1}
      rows={props.numLines}
      fullWidth />
  );

  return (
    <Stack
      spacing={2}
      direction="row"
      alignItems="center">
      {props.startAdornment && (
        <Box>
          {props.startAdornment}
        </Box>
      )}

      <Box style={{ flex: 1 }}>
        {_.isString(props.label) ? (
          <InputLabel isDisabled={props.isDisabled}>
            {props.label}
          </InputLabel>
        ) : props.label}

        {field}
      </Box>
    </Stack>
  );
};



export interface ITextFieldControllerProps<TFormState extends FieldValues> {
  formControl: Control<TFormState>;
  fieldKey: FieldPath<TFormState>;
  isRequired?: boolean;
  label: React.ReactNode;
  type?: ITextFieldProps['type'];
  isDisabled?: boolean;
  numLines?: number;
  characterCount?: number;
  placeholder?: string;
  extraValidationRules?: UseControllerProps['rules'];
  startAdornment?: React.ReactNode;
}

export function genTextFieldControllerComponent<TFormState extends FieldValues>(): React.FC<ITextFieldControllerProps<TFormState>> {
  const TextFieldController: React.FC<ITextFieldControllerProps<TFormState>> = props => {
    const [hasReachedLimit, setHasReachedLimit] = React.useState(false);
    const characterCount = React.useMemo(() =>
      props.characterCount ? 250 - props.characterCount : 250,
    [props.characterCount]);



    const isCharacterCountLessThanZero = React.useMemo(() => {
      if (characterCount < 0) {
        setHasReachedLimit(true);

      } else if (characterCount > 0 && hasReachedLimit) {
        setHasReachedLimit(false);

      }

      return characterCount < 0;

    }, [characterCount, hasReachedLimit]);



    return (
      <Stack
        direction="column"
        alignItems="center"
        spacing={2}>
        <Controller
          name={props.fieldKey}
          control={props.formControl}
          rules={{
            ...props.extraValidationRules,
            required: props.isRequired
          }}
          render={({ field, fieldState }) => (
            <Box width="100%">
              <TextField
                label={props.label}
                type={props.type}
                value={field.value}
                onChange={field.onChange}
                placeholder={props.placeholder}
                numLines={props.numLines}
                errorMessage={(fieldState.error)?.message}
                isDisabled={props.isDisabled}
                startAdornment={props.startAdornment} />
            </Box>
          )} />

        <CharacterCountBox width="100%">
          {!_.isUndefined(props.characterCount) &&
            <CharacterCountTypography
              hasReachedCharacterLimit={hasReachedLimit}
              align="right">
              {`${isCharacterCountLessThanZero ? 0 : characterCount}/250 characters left`}
            </CharacterCountTypography>
          }
        </CharacterCountBox>
      </Stack>
    );
  };

  return TextFieldController;
}
